/**
 * API Configuration
 * Centralizes all API-related constants and endpoints
 *
 * Note: All API requests use relative paths (/api/*)
 * Nginx handles proxying to the backend server
 */

/**
 * API Endpoints (relative paths)
 * All paths start with /api and will be proxied by nginx
 */
export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH: {
    LOGIN: '/api/admin/auth/login',
    REFRESH: '/api/admin/auth/refresh',
    LOGOUT: '/api/admin/auth/logout',
    ME: '/api/admin/auth/me',
  },

  // Admin Users endpoints
  USERS: {
    LIST: '/api/admin/users',
    GET: (id: string) => `/api/admin/users/${id}`,
    DETAILS: (id: string) => `/api/admin/users/${id}/details`,
    CREATE: '/api/admin/users',
    UPDATE: (id: string) => `/api/admin/users/${id}`,
    DELETE: (id: string) => `/api/admin/users/${id}`,
    STATS: '/api/admin/users/stats',
    PENDING_COUNT: '/api/admin/users/pending-count',
    BLOCK: (id: string) => `/api/admin/users/${id}/block`,
    UNBLOCK: (id: string) => `/api/admin/users/${id}/unblock`,
    CONFIRM: (id: string) => `/api/admin/users/${id}/confirm`,
    CHANGE_ROLE: (id: string) => `/api/admin/users/${id}/role`,
    BIND_REFERRAL: (id: string) => `/api/admin/users/${id}/bind-referral`,
    SEARCH_FOR_BINDING: (query: string) =>
      `/api/admin/users/search-for-binding?query=${encodeURIComponent(query)}`,
    RESET_PASSWORD: (id: string) =>
      `/api/admin/auth/users/${id}/reset-password`,
  },

  // Admin Dashboard & Reports
  ADMIN: {
    DASHBOARD: '/api/admin/reports/dashboard',
  },

  // Reports endpoints
  REPORTS: {
    DASHBOARD: '/api/admin/reports/dashboard',
    PARTNERS: '/api/admin/reports/partners',
    PARTNERS_EXPORT: '/api/admin/reports/partners/export',
    WITHDRAWALS: '/api/admin/reports/withdrawals',
    WITHDRAWALS_EXPORT: '/api/admin/reports/withdrawals/export',
    PROMOTIONS: '/api/admin/reports/promotions',
    PROMOTIONS_EXPORT: '/api/admin/reports/promotions/export',
  },

  // Promotions endpoints
  PROMOTIONS: {
    LIST: '/api/admin/promotions',
    STATS: '/api/admin/promotions/stats',
    GET: (id: string) => `/api/admin/promotions/${id}`,
    GET_DETAILS: (id: string) => `/api/admin/promotions/${id}/details`,
    CREATE: '/api/admin/promotions',
    UPDATE: (id: string) => `/api/admin/promotions/${id}`,
    DELETE: (id: string) => `/api/admin/promotions/${id}`,
    ARCHIVE: (id: string) => `/api/admin/promotions/${id}/archive`,
    BATCH_ACTIVATE: '/api/admin/promotions/batch-activate',
    BATCH_DEACTIVATE: '/api/admin/promotions/batch-deactivate',
    BATCH_ARCHIVE: '/api/admin/promotions/batch-archive',
    REORDER: '/api/admin/promotions/reorder',
  },

  // Content pages (J1 — редактируемые тексты страниц)
  CONTENT_PAGES: {
    LIST: '/api/admin/content-pages',
    GET: (slug: string) => `/api/admin/content-pages/${slug}`,
    UPDATE: (slug: string) => `/api/admin/content-pages/${slug}`,
  },

  WITHDRAWALS: {
    LIST: '/api/admin/withdrawals',
    STATS: '/api/admin/withdrawals/stats',
    GET: (id: string) => `/api/admin/withdrawals/${id}`,
    SET_IN_REVIEW: (id: string) => `/api/admin/withdrawals/${id}/in-review`,
    APPROVE: (id: string) => `/api/admin/withdrawals/${id}/approve`,
    REJECT: (id: string) => `/api/admin/withdrawals/${id}/reject`,
    BATCH_REJECT: '/api/admin/withdrawals/batch-reject',
    EXPORT_1C: '/api/admin/withdrawals/export-1c',
    EXPORT_SINGLE: (id: string) => `/api/admin/withdrawals/${id}/export`,
    RECEIPT: (id: string) => `/api/admin/withdrawals/${id}/receipt`,
  },

  PARTICIPATIONS: {
    LIST: '/api/admin/participations',
    STATS: '/api/admin/participations/stats',
    GET: (id: string) => `/api/admin/participations/${id}`,
    APPROVE: (id: string) => `/api/admin/participations/${id}/approve`,
    REJECT: (id: string) => `/api/admin/participations/${id}/reject`,
    REVERT: (id: string) => `/api/admin/participations/${id}/revert`,
    BY_PROMOTION: (promotionId: string) =>
      `/api/admin/participations/promotions/${promotionId}/participants`,
  },

  FORM_SUBMISSIONS: {
    BY_PROMOTION: (promotionId: string) =>
      `/api/admin/form-submissions/promotions/${promotionId}`,
  },

  DOCUMENTS: {
    LIST: '/api/admin/documents',
    GET: (id: string) => `/api/admin/documents/${id}`,
    APPROVE: (id: string) => `/api/admin/documents/${id}/approve`,
    REJECT: (id: string) => `/api/admin/documents/${id}/reject`,
    REQUEST_REVISION: (id: string) =>
      `/api/admin/documents/${id}/request-revision`,
  },

  REVIEWS: {
    LIST: '/api/admin/reviews',
    STATS: '/api/admin/reviews/stats',
    BY_PROMOTION: (promotionId: string) =>
      `/api/admin/reviews/promotion/${promotionId}`,
    GET: (id: string) => `/api/admin/reviews/${id}`,
    APPROVE: (id: string) => `/api/admin/reviews/${id}/approve`,
    REJECT: (id: string) => `/api/admin/reviews/${id}/reject`,
    DELETE: (id: string) => `/api/admin/reviews/${id}`,
  },

  // Recommendations endpoints
  RECOMMENDATIONS: {
    LIST: '/api/admin/recommendations',
    STATS: '/api/admin/recommendations/stats',
    GET: (id: string) => `/api/admin/recommendations/${id}`,
    UPDATE: (id: string) => `/api/admin/recommendations/${id}`,
  },

  // Events (System Notifications) endpoints
  EVENTS: {
    LIST: '/api/admin/events',
    STATS: '/api/admin/events/stats',
    UNREAD_COUNT: '/api/admin/events/unread-count',
  },

  // Notifications (User Notifications) endpoints
  NOTIFICATIONS: {
    LIST: '/api/notifications',
    UNREAD_COUNT: '/api/notifications/unread-count',
    MARK_READ: (id: string) => `/api/notifications/${id}/read`,
    MARK_ALL_READ: '/api/notifications/read-all',
  },

  // Partner Applications endpoints
  PARTNER_APPLICATIONS: {
    LIST: '/api/admin/partner-applications',
    STATS: '/api/admin/partner-applications/stats',
    GET: (id: string) => `/api/admin/partner-applications/${id}`,
    SET_IN_REVIEW: (id: string) =>
      `/api/admin/partner-applications/${id}/in-review`,
    APPROVE: (id: string) => `/api/admin/partner-applications/${id}/approve`,
    REJECT: (id: string) => `/api/admin/partner-applications/${id}/reject`,
    REVERT: (id: string) => `/api/admin/partner-applications/${id}/revert`,
  },

  // Profile Moderation endpoints
  PROFILE_MODERATION: {
    LIST: '/api/admin/profile-moderation',
    GET: (id: string) => `/api/admin/profile-moderation/${id}`,
    REVIEW: (id: string) => `/api/admin/profile-moderation/${id}/review`,
  },

  // Bonuses & Levels endpoints
  BONUSES: {
    ACCOUNTS: '/api/admin/bonuses/accounts',
    ACCOUNT: (id: string) => `/api/admin/bonuses/accounts/${id}`,
    TRANSACTION: '/api/admin/bonuses/transaction',
    LEVELS: '/api/admin/bonuses/levels',
    LEVEL: (id: string) => `/api/admin/bonuses/levels/${id}`,
  },

  // Achievements endpoints
  ACHIEVEMENTS: {
    LIST: '/api/admin/achievements',
    GET: (id: string) => `/api/admin/achievements/${id}`,
    CREATE: '/api/admin/achievements',
    UPDATE: (id: string) => `/api/admin/achievements/${id}`,
    DELETE: (id: string) => `/api/admin/achievements/${id}`,
  },

  // Settings endpoints
  SETTINGS: {
    LIST: '/api/admin/settings',
    GROUPED: '/api/admin/settings/grouped',
    GET: (key: string) => `/api/admin/settings/${key}`,
    UPDATE: (key: string) => `/api/admin/settings/${key}`,
    RESET: (key: string) => `/api/admin/settings/${key}/reset`,
  },

  REFERRALS: {
    LIST: '/api/admin/referrals',
    BIND: '/api/admin/referrals/bind',
    ALLOWED_DOMAINS: '/api/admin/referrals/allowed-domains',
    ALLOWED_DOMAIN: (id: string) =>
      `/api/admin/referrals/allowed-domains/${id}`,
  },

  SMS: {
    PROVIDERS: '/api/admin/sms/providers',
    PROVIDER: (id: string) => `/api/admin/sms/providers/${id}`,
    LOGS: '/api/admin/sms/logs',
    STATS: '/api/admin/sms/stats',
    SEND_TEST: '/api/admin/sms/send-test',
  },

  FILES: {
    UPLOAD: '/api/files/upload',
    GET: (id: string) => `/api/files/${id}`,
  },

  SUCCESS_STORIES: {
    LIST: '/api/admin/success-stories',
    GET: (id: string) => `/api/admin/success-stories/${id}`,
    CREATE: '/api/admin/success-stories',
    UPDATE: (id: string) => `/api/admin/success-stories/${id}`,
    DELETE: (id: string) => `/api/admin/success-stories/${id}`,
  },

  CHAT: {
    CONVERSATIONS: '/api/admin/chat/conversations',
    CONVERSATION: (id: string) => `/api/admin/chat/conversations/${id}`,
    MESSAGES: '/api/admin/chat/messages',
    MARK_READ: (id: string) => `/api/admin/chat/conversations/${id}/read`,
    CLOSE: (id: string) => `/api/admin/chat/conversations/${id}/close`,
    STATS: '/api/admin/chat/stats',
  },
} as const;

export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
} as const;
